<script setup>
import { computed } from 'vue'
import { useBudgetData } from '../composables/useBudgetData'

const { currentMonthTransactions, dailyBudget, currentDate, isViewingCurrentMonth } = useBudgetData()

const averageSpending = computed(() => {
  const viewingDate = currentDate.value;
  const currentYear = viewingDate.getFullYear();
  const currentMonth = viewingDate.getMonth();
  
  // For past months, calculate average across all days in the month
  // For current month, calculate average up to today
  const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
  const daysToCalculate = isViewingCurrentMonth.value 
    ? viewingDate.getDate() 
    : daysInMonth;

  if (daysToCalculate === 0) return 0;

  let totalSpending = 0;

  // Calculate spending for each day of the month
  for (let day = 1; day <= daysToCalculate; day++) {
    // Get transactions for this specific day
    const dayTransactions = currentMonthTransactions.value.filter(t => {
      const transactionDate = new Date(t.date);
      return transactionDate.getDate() === day &&
        transactionDate.getMonth() === currentMonth &&
        transactionDate.getFullYear() === currentYear;
    });

    // Only count debits (actual spending), exclude savings and credits
    const daySpending = dayTransactions
      .filter(t => t.type === 'debit')
      .reduce((sum, t) => sum + t.amount, 0);

    totalSpending += daySpending;
  }

  return totalSpending / daysToCalculate;
})

const spendingPerformance = computed(() => {
  if (dailyBudget.value === 0) return 0;
  return Math.round((averageSpending.value / dailyBudget.value) * 100);
})

const isOverBudget = computed(() => {
  return averageSpending.value > dailyBudget.value;
})

const budgetRemaining = computed(() => {
  return Math.max(0, dailyBudget.value - averageSpending.value);
})
</script>

<template>
  <div class="average-spending-card card spending-good">

    <!-- Card Header -->
    <div class="card-header">
      <div class="header-icon">
        <i class="bi bi-credit-card"></i>
      </div>
      <div class="header-content">
        <h3 class="card-title">Average Daily Spending</h3>
        <p class="card-subtitle">Your spending pattern</p>
      </div>
    </div>

    <!-- Card Body -->
    <div class="card-body">
      <div class="row mb-2">
        <div class="col">
          <label for="transactionDescription" class="form-label fw-semibold">
            <i class="fa-solid fa-dollar-sign me-1"></i>Typical Daily Spending
          </label>
        </div>
      </div>
      <div class="row">
        <div class="col text-center">
          <span class="amount-value">{{ averageSpending.toFixed(2) }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.spending-over .header-icon {
  background: linear-gradient(135deg, var(--light-danger), #ff6b6b);
}

.header-content {
  flex: 1;
  text-align: left;
}

/* Amount Section */
.amount-section {
  margin-bottom: 1.5rem;
}

.amount-container {
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 0.25rem;
  margin-bottom: 0.75rem;
}

.currency-symbol {
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--light-text-muted);
}

.amount-status {
  margin-top: 0.5rem;
}



/* Budget Section */
.budget-section {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.5rem;
  padding: 1rem;
  background: rgba(0, 0, 0, 0.02);
  border-radius: 12px;
}

.budget-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex: 1;
}

.budget-icon {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  flex-shrink: 0;
}

.target-icon {
  background: rgba(13, 110, 253, 0.1);
  color: #0d6efd;
}

.remaining-icon {
  background: rgba(40, 167, 69, 0.1);
  color: var(--light-success);
}

.budget-content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  min-width: 0;
}

.budget-label {
  font-size: 0.75rem;
  color: var(--light-text-muted);
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  line-height: 1;
}

.budget-value {
  font-size: 1rem;
  font-weight: 700;
  color: var(--light-text);
  margin-top: 0.25rem;
  line-height: 1;
}

.remaining-value {
  color: var(--light-success);
}

.budget-divider {
  width: 1px;
  height: 40px;
  background: var(--light-border);
  margin: 0 1rem;
  flex-shrink: 0;
}

/* Progress Section */
.progress-section {
  margin-bottom: 1rem;
}

.progress-bar-container {
  width: 100%;
  height: 8px;
  background: var(--light-border);
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 0.75rem;
}

.progress-bar {
  height: 100%;
  border-radius: 4px;
  transition: all 0.6s ease;
  background: var(--light-success);
}

.progress-labels {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.progress-label {
  font-size: 0.8rem;
  color: var(--light-text-muted);
  font-weight: 500;
}

.progress-percentage {
  font-size: 0.8rem;
  font-weight: 700;
  color: var(--light-text);
}

.progress-percentage.over-budget {
  color: var(--light-danger);
}

/* Trend Section */
.trend-section {
  margin-top: 1rem;
}

.trend-indicator {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: 5px;
  font-size: 0.8rem;
  font-weight: 600;
}

.trend-good {
  background: rgba(40, 167, 69, 0.1);
  color: var(--light-success);
  border: 1px solid rgba(40, 167, 69, 0.2);
}

.trend-warning {
  background: rgba(255, 193, 7, 0.1);
  color: #ffc107;
  border: 1px solid rgba(255, 193, 7, 0.2);
}

.trend-danger {
  background: rgba(220, 53, 69, 0.1);
  color: var(--light-danger);
  border: 1px solid rgba(220, 53, 69, 0.2);
}

.trend-text {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Dark Mode Adjustments */
:global([data-theme]) .budget-section {
  background: rgba(255, 255, 255, 0.05);
}

:global([data-theme]) .progress-bar-container {
  background: var(--light-border);
}

/* Responsive Design */
@media (max-width: 768px) {
  .currency-symbol {
    font-size: 1.25rem;
  }

  .budget-section {
    flex-direction: column;
    gap: 1rem;
    padding: 0.75rem;
  }

  .budget-item {
    width: 100%;
    justify-content: center;
  }

  .budget-divider {
    width: 100%;
    height: 1px;
    margin: 0;
  }

  .budget-content {
    align-items: center;
    text-align: center;
  }
}
</style>